import { Box2, Camera, Matrix4, Vector2, Vector4 } from "three";

export const SURFACE_CALLOUT_MARGIN = 12;
const CALLOUT_GAP = 24;

/**
 * @param outline Local-space outline segments.
 * @param worldMatrix Outline's world transform.
 * @param camera Current view camera.
 * @param viewport Canvas dimensions in pixels.
 * @param label Measured callout dimensions in pixels.
 * @returns Callout top-left and leader endpoints relative to it, or undefined behind the camera.
 */
export function positionSurfaceCallout(
  outline: Float32Array,
  worldMatrix: Matrix4,
  camera: Camera,
  viewport: { width: number; height: number },
  label: { width: number; height: number },
): { position: Vector2; anchor: Vector2; connector: Vector2 } | undefined {
  const project = new Matrix4()
    .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
    .multiply(worldMatrix);
  const bounds = new Box2();
  const vertices: Vector2[] = [];
  const start = new Vector4();
  const end = new Vector4();
  for (let offset = 0; offset + 5 < outline.length; offset += 6) {
    start
      .set(outline[offset], outline[offset + 1], outline[offset + 2], 1)
      .applyMatrix4(project);
    end
      .set(outline[offset + 3], outline[offset + 4], outline[offset + 5], 1)
      .applyMatrix4(project);
    const startDistance = start.z + start.w;
    const endDistance = end.z + end.w;
    if (startDistance < 0 && endDistance < 0) continue;
    if (startDistance < 0) {
      start.lerp(end, startDistance / (startDistance - endDistance));
    } else if (endDistance < 0) {
      end.lerp(start, endDistance / (endDistance - startDistance));
    }
    for (const point of [start, end]) {
      if (point.w <= 0) continue;
      const screen = new Vector2(
        ((point.x / point.w + 1) * viewport.width) / 2,
        ((1 - point.y / point.w) * viewport.height) / 2,
      );
      if (!Number.isFinite(screen.x) || !Number.isFinite(screen.y)) continue;
      vertices.push(screen);
      bounds.expandByPoint(screen);
    }
  }
  if (bounds.isEmpty()) return;
  const margin = SURFACE_CALLOUT_MARGIN;
  const width = Math.min(label.width, Math.max(0, viewport.width - 2 * margin));
  const height = Math.min(
    label.height,
    Math.max(0, viewport.height - 2 * margin),
  );
  const minimum = new Vector2(margin, margin);
  const maximum = new Vector2(
    Math.max(margin, viewport.width - margin - width),
    Math.max(margin, viewport.height - margin - height),
  );
  const screenMaximum = new Vector2(viewport.width, viewport.height);
  const visibleBounds = new Box2(
    bounds.min.clone().clamp(new Vector2(), screenMaximum),
    bounds.max.clone().clamp(new Vector2(), screenMaximum),
  );
  const center = visibleBounds.getCenter(new Vector2());
  const right = new Vector2(bounds.max.x + CALLOUT_GAP, center.y - height / 2);
  const left = new Vector2(
    bounds.min.x - CALLOUT_GAP - width,
    center.y - height / 2,
  );
  const sides =
    viewport.width - bounds.max.x >= bounds.min.x
      ? [right, left]
      : [left, right];
  const candidates = [
    ...sides,
    new Vector2(center.x - width / 2, bounds.max.y + CALLOUT_GAP),
    new Vector2(center.x - width / 2, bounds.min.y - CALLOUT_GAP - height),
  ];
  let best = candidates[0].clone().clamp(minimum, maximum);
  let leastOverlap = Infinity;
  for (const candidate of candidates) {
    candidate.clamp(minimum, maximum);
    const overlapWidth = Math.max(
      0,
      Math.min(candidate.x + width, visibleBounds.max.x) -
        Math.max(candidate.x, visibleBounds.min.x),
    );
    const overlapHeight = Math.max(
      0,
      Math.min(candidate.y + height, visibleBounds.max.y) -
        Math.max(candidate.y, visibleBounds.min.y),
    );
    const overlap = overlapWidth * overlapHeight;
    if (overlap < leastOverlap) {
      leastOverlap = overlap;
      best = candidate;
    }
    if (overlap === 0) break;
  }
  const labelBounds = new Box2(
    best,
    best.clone().add(new Vector2(width, height)),
  );
  let anchor = vertices[0];
  let connector = labelBounds.clampPoint(anchor, new Vector2());
  let distance = anchor.distanceToSquared(connector);
  for (const vertex of vertices) {
    const nearest = labelBounds.clampPoint(vertex, new Vector2());
    const nextDistance = vertex.distanceToSquared(nearest);
    if (nextDistance < distance) {
      anchor = vertex;
      connector = nearest;
      distance = nextDistance;
    }
  }
  return {
    position: best,
    anchor: anchor.clone().sub(best),
    connector: connector.sub(best),
  };
}
